import { FC, useEffect, useRef, useState } from "react";
import { Lock, Wifi } from "lucide-react";
import axios from "axios";
import UserAvatar from "../user-avatar/UserAvatar";

import CustomModal from "../custom-modal/custom-modal";
import {
  GET_MANAGERS_WITH_ACCESS_CODE,
  VERIFY_AND_LOG_MANAGER_ACCESS,
  VERIFY_AND_LOG_NFC_ACCESS,
} from "../../../configs/apiConfig";
import useNfcReader from "../../../hooks/useNfcReader";
import { Colors } from "../../../configs/colors";

interface IManager {
  id: number;
  firstName: string;
  lastName: string;
  avatar: string | null;
}

interface IManagerCodeModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  task: string;
  onDone: (managerId?: number) => void;
  requestedByUserId?: number;
  actionType?: string;
  cashierPointId?: number;
  storeId?: number;
  /** ID of the bill this authorization applies to, when the action being gated
   * is scoped to a single invoice (e.g. void, refund, edit). Omit/undefined
   * for actions that are not bill-scoped (cash register access, reports, etc.). */
  billId?: number;
}

const PRESTO = {
  primary: Colors.PRIMARY,
  primaryDark: "#016d41",
};

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function ManagerTile({
  manager,
  selected,
  onSelect,
}: {
  manager: IManager;
  selected: boolean;
  onSelect: (id: number) => void;
}) {
  const fullName = `${manager.firstName} ${manager.lastName}`;

  return (
    <button
      type="button"
      onClick={() => onSelect(manager.id)}
      className={cn(
        "rounded-lg border px-1.5 py-1.5 text-center transition-all",
        selected
          ? "border-gray-300 bg-gray-100"
          : "border-transparent bg-white hover:bg-gray-100",
      )}
    >
      <div className="mx-auto mb-1.5 flex justify-center">
        <UserAvatar
          src={manager.avatar}
          name={fullName}
          id={manager.id}
          size={48}
          selected={selected}
        />
      </div>

      <p className="line-clamp-2 text-xs font-semibold leading-tight text-gray-800">
        {fullName}
      </p>
      <p className="mt-0.5 text-[0.625rem] text-gray-500">ID: {manager.id}</p>
    </button>
  );
}

const ManagerCodeModal: FC<IManagerCodeModalProps> = ({
  open,
  onClose,
  title,
  task,
  onDone,
  requestedByUserId,
  actionType = "CASH_REGISTER_ACCESS",
  cashierPointId,
  storeId,
  billId,
}) => {
  const [managers, setManagers] = useState<IManager[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [enteredCode, setEnteredCode] = useState("");
  const [codeError, setCodeError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Track whether the last keystroke sequence was rapid (HID NFC reader)
  const lastKeyTimeRef = useRef(0);
  const rapidNfcRef = useRef(false);
  // Mirror of submitting in a ref so the NFC effect always reads the latest value
  const submittingRef = useRef(false);

  const setSubmittingState = (val: boolean) => {
    submittingRef.current = val;
    setSubmitting(val);
  };

  const scannedCard = useNfcReader();
  const token = () => localStorage.getItem("token");

  // Fetch managers when the modal opens
  useEffect(() => {
    if (!open) return;
    setManagersLoading(true);
    axios
      .get(GET_MANAGERS_WITH_ACCESS_CODE, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      .then((res) => {
        const list: IManager[] = res.data?.body ?? [];
        setManagers(list);
        setSelectedManagerId((cur) => {
          if (cur && list.some((m) => m.id === cur)) return cur;
          return list[0]?.id ?? null;
        });
      })
      .catch(() => setManagers([]))
      .finally(() => setManagersLoading(false));
  }, [open]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSelectedManagerId(null);
      setEnteredCode("");
      setCodeError(false);
    } else {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  // NFC card silent authentication — fires for both tray-app HTTP and HID keyboard reader.
  // Uses submittingRef (not the stale state closure) so it can't be blocked by a concurrent
  // handleSubmit that was accidentally triggered by the HID Enter keystroke.
  useEffect(() => {
    if (!open || !scannedCard || submittingRef.current || requestedByUserId == null) return;

    // Clear any NFC data that landed in the PIN field and proceed as NFC auth
    setEnteredCode("");
    setCodeError(false);
    setSubmittingState(true);

    axios
      .post(
        VERIFY_AND_LOG_NFC_ACCESS,
        { nfcCardId: scannedCard, requestedByUserId, actionType, cashierPointId, storeId, billId },
        { headers: { Authorization: `Bearer ${token()}` } },
      )
      .then((res) => {
        if (res.data?.status === "SUCCESS") onDone(res.data?.body?.grantedById);
        else setCodeError(true);
      })
      .catch(() => setCodeError(true))
      .finally(() => setSubmittingState(false));
  }, [scannedCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectManager = (id: number) => {
    setSelectedManagerId(id);
    setEnteredCode("");
    setCodeError(false);
  };

  const handleSubmit = async () => {
    if (!selectedManagerId || !enteredCode.trim() || submittingRef.current) return;

    setSubmittingState(true);
    setCodeError(false);

    try {
      const res = await axios.post(
        VERIFY_AND_LOG_MANAGER_ACCESS,
        { managerId: selectedManagerId, enteredCode, requestedByUserId, actionType, cashierPointId, storeId, billId },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      if (res.data?.status === "SUCCESS") onDone(res.data?.body?.grantedById ?? selectedManagerId);
      else setCodeError(true);
    } catch {
      setCodeError(true);
    } finally {
      setSubmittingState(false);
    }
  };

  const handleNfcTap = () => {
    if (!selectedManagerId || submittingRef.current || requestedByUserId == null) return;
    const manager = managers.find((m) => m.id === selectedManagerId);
    if (!manager) return;

    setSubmittingState(true);
    setCodeError(false);

    axios
      .post(
        VERIFY_AND_LOG_NFC_ACCESS,
        { nfcCardId: manager.id.toString(), requestedByUserId, actionType, cashierPointId, storeId, billId },
        { headers: { Authorization: `Bearer ${token()}` } },
      )
      .then((res) => {
        if (res.data?.status === "SUCCESS") onDone(res.data?.body?.grantedById ?? selectedManagerId);
        else setCodeError(true);
      })
      .catch(() => setCodeError(true))
      .finally(() => setSubmittingState(false));
  };

  const canSubmitPin = enteredCode.trim().length > 0 && selectedManagerId !== null && !submitting;
  const canSubmitNfc = selectedManagerId !== null && !submitting && requestedByUserId != null;

  return (
    <CustomModal
      title={title}
      openModal={open}
      onClose={onClose}
      width="28rem"
      height="auto"
      disableBackdropClick={true}
      enableBlurBackground={true}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", paddingBottom: "0.5rem" }}>
        {/* Instruction */}
        <p style={{ fontSize: "0.875rem", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
          This action requires manager approval. To {task}, select your name then enter your PIN or
          tap your manager access badge.
        </p>

        {/* Manager grid */}
        {managersLoading ? (
          <div className="flex justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
          </div>
        ) : managers.length > 0 ? (
          <div>
            <p style={{ fontSize: "0.75rem", fontWeight: 500, color: "#374151", marginBottom: "0.5rem" }}>
              Select Manager
            </p>
            <div className="grid grid-cols-4 gap-1.5 rounded-xl bg-gray-50 p-1.5">
              {managers.map((manager) => (
                <ManagerTile
                  key={manager.id}
                  manager={manager}
                  selected={selectedManagerId === manager.id}
                  onSelect={handleSelectManager}
                />
              ))}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: "0.875rem", color: "#9ca3af", textAlign: "center", fontStyle: "italic" }}>
            No managers with access codes configured.
          </p>
        )}

        {/* PIN input */}
        <div>
          <label
            htmlFor="manager-pin"
            style={{ display: "block", fontSize: "0.75rem", fontWeight: 500, color: "#374151", marginBottom: "0.375rem" }}
          >
            Enter PIN
          </label>
          <div className="relative">
            <Lock
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              id="manager-pin"
              ref={inputRef}
              type="password"
              autoComplete="new-password"
              value={enteredCode}
              onChange={(e) => {
                setEnteredCode(e.target.value);
                setCodeError(false);
              }}
              onKeyDown={(e) => {
                const now = Date.now();
                if (e.key !== "Enter") {
                  // Track whether keystrokes are arriving faster than a human can type
                  rapidNfcRef.current =
                    lastKeyTimeRef.current !== 0 &&
                    now - lastKeyTimeRef.current < 50;
                  lastKeyTimeRef.current = now;
                } else {
                  // If the preceding keystrokes were rapid, this Enter is from the
                  // HID NFC reader — let useNfcReader handle it, don't treat as PIN
                  if (!rapidNfcRef.current) handleSubmit();
                  lastKeyTimeRef.current = 0;
                  rapidNfcRef.current = false;
                }
              }}
              style={{
                width: "100%",
                height: "2.75rem",
                borderRadius: "0.5rem",
                border: codeError ? "1px solid #ef4444" : "1px solid #d1d5db",
                backgroundColor: "#f9fafb",
                paddingLeft: "2.25rem",
                paddingRight: "0.75rem",
                fontSize: "0.875rem",
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.15s, background-color 0.15s",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#9ca3af";
                e.currentTarget.style.backgroundColor = "#fff";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = codeError ? "#ef4444" : "#d1d5db";
                e.currentTarget.style.backgroundColor = "#f9fafb";
              }}
              placeholder="Enter PIN"
            />
          </div>
          {codeError && (
            <p style={{ marginTop: "0.375rem", fontSize: "0.75rem", color: "#ef4444" }}>
              Invalid access code. Please try again.
            </p>
          )}
        </div>

        {/* Confirm PIN button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmitPin}
          style={{
            width: "100%",
            borderRadius: "0.5rem",
            padding: "0.75rem",
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#fff",
            backgroundColor: PRESTO.primary,
            border: "none",
            cursor: canSubmitPin ? "pointer" : "not-allowed",
            opacity: canSubmitPin ? 1 : 0.4,
            transition: "background-color 0.15s, opacity 0.15s",
          }}
          onMouseEnter={(e) => {
            if (!canSubmitPin) return;
            e.currentTarget.style.backgroundColor = PRESTO.primaryDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PRESTO.primary;
          }}
        >
          {submitting ? "Verifying…" : "Confirm Access"}
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
          <span style={{ fontSize: "0.625rem", fontWeight: 500, textTransform: "uppercase" as const, letterSpacing: "0.1em", color: "#9ca3af" }}>
            or
          </span>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
        </div>

        {/* NFC tap button */}
        <button
          type="button"
          onClick={handleNfcTap}
          disabled={!canSubmitNfc}
          style={{
            width: "100%",
            borderRadius: "0.5rem",
            border: "1px dashed #d1d5db",
            padding: "0.625rem 0.75rem",
            textAlign: "left",
            backgroundColor: "#f9fafb",
            cursor: canSubmitNfc ? "pointer" : "not-allowed",
            opacity: canSubmitNfc ? 1 : 0.6,
            transition: "background-color 0.15s",
          }}
          onMouseEnter={(e) => {
            if (!canSubmitNfc) return;
            e.currentTarget.style.backgroundColor = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div
              style={{
                position: "relative",
                width: "2rem",
                height: "2rem",
                borderRadius: "50%",
                border: "1px solid #e5e7eb",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Wifi size={14} color="#6b7280" />
            </div>
            <div>
              <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "#4b5563", lineHeight: 1.3, margin: 0 }}>
                Tap Manager Access Badge
              </p>
              <p style={{ fontSize: "0.625rem", color: "#9ca3af", lineHeight: 1.4, margin: 0 }}>
                Hold card near reader
              </p>
            </div>
          </div>
        </button>

        <p style={{ textAlign: "center", fontSize: "0.625rem", color: "#9ca3af", lineHeight: 1.5, margin: 0 }}>
          Use PIN or tap the assigned NFC card to approve this action.
        </p>
      </div>
    </CustomModal>
  );
};

export default ManagerCodeModal;
